import { useState, useEffect, useMemo, useCallback } from "react";

// ─── STORAGE SHIM ───────────────────────────────────────────────────────────
// Uses artifact storage (window.storage) if available, falls back to
// localStorage for local Vite dev. Replaced by Supabase in Phase 2.

const storage = {
  async get(key: string) {
    try {
      if (typeof window !== 'undefined' && (window as any).storage?.get) {
        return await (window as any).storage.get(key);
      }
      const val = localStorage.getItem(key);
      return val ? { value: val } : null;
    } catch {
      return null;
    }
  },
  async set(key: string, value: string) {
    try {
      if (typeof window !== 'undefined' && (window as any).storage?.set) {
        return await (window as any).storage.set(key, value);
      }
      localStorage.setItem(key, value);
      return { key, value };
    } catch {
      return null;
    }
  },
};

// ─── DATA & CONSTANTS ───────────────────────────────────────────────────────

const VALUES_LIST = [
  { id: "sustainability", label: "Environmental Sustainability", icon: "🌿" },
  { id: "autonomy", label: "Personal Autonomy", icon: "🦅" },
  { id: "equality", label: "Equality & Equity", icon: "⚖️" },
  { id: "creativity", label: "Creative Expression", icon: "🎨" },
  { id: "spirituality", label: "Spiritual Growth", icon: "✦" },
  { id: "education", label: "Lifelong Learning", icon: "📚" },
  { id: "health", label: "Health & Wellness", icon: "💪" },
  { id: "simplicity", label: "Simple Living", icon: "🕊" },
  { id: "service", label: "Service to Others", icon: "🤝" },
  { id: "resilience", label: "Self-Sufficiency", icon: "🏡" },
  { id: "transparency", label: "Radical Transparency", icon: "🔍" },
  { id: "tradition", label: "Cultural Tradition", icon: "🪶" },
  { id: "innovation", label: "Innovation & Progress", icon: "⚡" },
  { id: "connection", label: "Deep Connection", icon: "💫" },
  { id: "justice", label: "Social Justice", icon: "✊" },
  { id: "nature", label: "Nature Immersion", icon: "🌲" },
];

const SKILLS_LIST = [
  { id: "farming", label: "Farming & Agriculture", cat: "land" },
  { id: "building", label: "Construction & Building", cat: "land" },
  { id: "cooking", label: "Cooking & Food Prep", cat: "domestic" },
  { id: "childcare", label: "Childcare & Education", cat: "domestic" },
  { id: "healing", label: "Healing & Medicine", cat: "care" },
  { id: "counseling", label: "Counseling & Mediation", cat: "care" },
  { id: "tech", label: "Technology & IT", cat: "modern" },
  { id: "finance", label: "Finance & Accounting", cat: "modern" },
  { id: "art", label: "Art & Music", cat: "creative" },
  { id: "writing", label: "Writing & Communication", cat: "creative" },
  { id: "leadership", label: "Facilitation & Leadership", cat: "governance" },
  { id: "legal", label: "Legal & Compliance", cat: "governance" },
  { id: "permaculture", label: "Permaculture Design", cat: "land" },
  { id: "energy", label: "Renewable Energy", cat: "land" },
  { id: "crafts", label: "Crafts & Handwork", cat: "creative" },
  { id: "mechanics", label: "Mechanics & Repair", cat: "land" },
];

const BIG_FIVE_QUESTIONS = [
  { trait: "openness", text: "I enjoy exploring unfamiliar ideas, cultures, and experiences", dir: 1 },
  { trait: "openness", text: "I prefer routine and predictability over novelty", dir: -1 },
  { trait: "conscientiousness", text: "I keep my living spaces organized and follow through on commitments", dir: 1 },
  { trait: "conscientiousness", text: "I tend to go with the flow rather than plan ahead", dir: -1 },
  { trait: "extraversion", text: "I feel energized after spending time in group activities", dir: 1 },
  { trait: "extraversion", text: "I need significant alone time to recharge", dir: -1 },
  { trait: "agreeableness", text: "I prioritize group harmony, even if it means compromising my preferences", dir: 1 },
  { trait: "agreeableness", text: "I speak my mind directly, even if others might disagree", dir: -1 },
  { trait: "neuroticism", text: "I often worry about things that might go wrong", dir: 1 },
  { trait: "neuroticism", text: "I stay calm and steady under pressure", dir: -1 },
];

// ─── MATCHING ALGORITHM ─────────────────────────────────────────────────────
// Extracted to src/lib/matching.ts for testability.
// Inline copy kept here for artifact standalone mode.
// In production build, delete this and import from '../lib/matching'.

function computeCompatibility(profileA: any, profileB: any) {
  // 1. Personality similarity (Big Five) — weighted 25%
  const traits = ["openness", "conscientiousness", "extraversion", "agreeableness", "neuroticism"];
  let personalityScore = 0;
  traits.forEach((t) => {
    const diff = Math.abs(profileA.personality[t] - profileB.personality[t]) / 6; // normalized 0-1
    // For neuroticism, complementary is better (one high one low)
    if (t === "neuroticism") {
      personalityScore += diff * 0.5 + (1 - diff) * 0.5; // partial credit for difference
    } else {
      personalityScore += 1 - diff;
    }
  });
  personalityScore /= traits.length;

  // 2. Values alignment — weighted 35%
  const sharedValues = profileA.values.filter((v) => profileB.values.includes(v));
  const valuesScore = sharedValues.length / Math.max(profileA.values.length, profileB.values.length, 1);

  // 3. Lifestyle compatibility — weighted 25%
  let lifestyleScore = 0;
  let lifestyleChecks = 0;
  // Sleep
  if (profileA.lifestyle.sleep === profileB.lifestyle.sleep) { lifestyleScore += 1; }
  else if (profileA.lifestyle.sleep === "flexible" || profileB.lifestyle.sleep === "flexible") { lifestyleScore += 0.7; }
  else { lifestyleScore += 0.2; }
  lifestyleChecks++;
  // Noise
  const noiseMap = { quiet: 1, moderate: 2, lively: 3 };
  lifestyleScore += 1 - Math.abs((noiseMap[profileA.lifestyle.noise] || 2) - (noiseMap[profileB.lifestyle.noise] || 2)) / 2;
  lifestyleChecks++;
  // Diet compatibility
  const dietCompat = { vegan: 3, vegetarian: 2, flexible: 1, omnivore: 0 };
  lifestyleScore += 1 - Math.abs((dietCompat[profileA.lifestyle.diet] ?? 1) - (dietCompat[profileB.lifestyle.diet] ?? 1)) / 3;
  lifestyleChecks++;
  // Cleanliness
  lifestyleScore += 1 - Math.abs((profileA.lifestyle.cleanliness || 3) - (profileB.lifestyle.cleanliness || 3)) / 4;
  lifestyleChecks++;
  // Social frequency
  const socialMap = { daily: 4, few: 3, weekly: 2, minimal: 1 };
  lifestyleScore += 1 - Math.abs((socialMap[profileA.lifestyle.social] || 2) - (socialMap[profileB.lifestyle.social] || 2)) / 3;
  lifestyleChecks++;
  // Privacy
  lifestyleScore += 1 - Math.abs((profileA.lifestyle.privacy || 3) - (profileB.lifestyle.privacy || 3)) / 4;
  lifestyleChecks++;
  // Shared meals
  lifestyleScore += profileA.lifestyle.meals === profileB.lifestyle.meals ? 1 : 0.4;
  lifestyleChecks++;
  lifestyleScore /= lifestyleChecks;

  // 4. Skills complementarity — weighted 15%
  const allSkills = new Set([...profileA.skills, ...profileB.skills]);
  const overlap = profileA.skills.filter((s) => profileB.skills.includes(s)).length;
  const unique = allSkills.size;
  // Reward diversity (complementary skills) more than overlap
  const diversityRatio = unique / Math.max(profileA.skills.length + profileB.skills.length, 1);
  const overlapBonus = overlap > 0 ? 0.3 : 0;
  const skillsScore = Math.min(diversityRatio + overlapBonus, 1);

  // Weighted total
  const total = personalityScore * 0.25 + valuesScore * 0.35 + lifestyleScore * 0.25 + skillsScore * 0.15;

  return {
    overall: Math.round(total * 100),
    personality: Math.round(personalityScore * 100),
    values: Math.round(valuesScore * 100),
    lifestyle: Math.round(lifestyleScore * 100),
    skills: Math.round(skillsScore * 100),
    sharedValues,
  };
}

// ─── GOVERNANCE ENGINE ──────────────────────────────────────────────────────

const GOVERNANCE_FRAMEWORKS = {
  band: {
    name: "Band",
    size: "5–15 people",
    dunbar: "Intimate support circle + sympathy group",
    model: "Kitchen Table Consensus",
    description: "At this scale, everyone knows everyone deeply. Governance can be informal but intentional. Decisions happen face-to-face with full participation.",
    structure: [
      { role: "Rotating Facilitator", desc: "A different person leads each weekly gathering. Prevents power concentration." },
      { role: "Shared Agreements Keeper", desc: "One person maintains the living document of community agreements." },
      { role: "Wellbeing Check-in", desc: "Weekly circle where each member shares their state. Early conflict detection." },
    ],
    decisionMaking: "Full consensus — every member must consent (no objections). For a group this small, this is sustainable. Use 'rounds' where each person speaks in turn.",
    conflictProtocol: [
      "1:1 conversation between parties within 48 hours",
      "If unresolved → facilitated dialogue with a neutral community member",
      "If still unresolved → full circle process with structured talking piece",
      "Nuclear option: community review of membership fit (requires 2/3 consent)",
    ],
    rituals: ["Weekly sharing circle", "Monthly retrospective on agreements", "Seasonal visioning day"],
    risks: ["Groupthink from closeness", "One dominant personality hijacking decisions", "Burnout from wearing too many hats"],
  },
  clan: {
    name: "Clan",
    size: "15–50 people",
    dunbar: "Extended friendship circle — you know everyone but not deeply",
    model: "Sociocratic Circles",
    description: "Too large for full-group consensus on everything. Sociocracy distributes decisions to domain-specific circles while keeping overall coherence through linking.",
    structure: [
      { role: "General Circle", desc: "Representatives from all domain circles. Sets policy, resolves cross-circle issues." },
      { role: "Domain Circles (4-7)", desc: "Self-governing teams: Land & Food, Housing, Finance, Wellbeing, Governance, Culture." },
      { role: "Double Links", desc: "Each circle sends both a leader and elected rep to the General Circle — ensures two-way information flow." },
      { role: "Membership Circle", desc: "Handles onboarding, integration, and exit processes." },
    ],
    decisionMaking: "Consent within circles (no objections, not full agreement). Policy decisions require circle consent. Operations delegated to circle leaders.",
    conflictProtocol: [
      "1:1 conversation within 48 hours",
      "Peer mediation from the Wellbeing circle",
      "Formal restorative circle with trained facilitator",
      "General Circle review for systemic issues",
      "Structured membership review process with clear criteria",
    ],
    rituals: ["Weekly circle meetings", "Monthly all-hands gathering (informational, not decisional)", "Quarterly community retrospective", "Annual vision renewal"],
    risks: ["Information silos between circles", "Membership circle becoming gatekeepers", "Meeting fatigue", "Free-rider problem with shared labor"],
  },
  tribe: {
    name: "Tribe",
    size: "50–150 people",
    dunbar: "The cognitive limit — you recognize everyone but can't track all relationships",
    model: "Nested Sociocracy + Elected Council",
    description: "At tribal scale, you need formal representative structures. Sub-groups (bands/clans within the tribe) have significant autonomy. The whole tribe gathers for ceremony and major decisions only.",
    structure: [
      { role: "Tribal Council", desc: "7-12 elected representatives from sub-communities. Sets overarching policy." },
      { role: "Sub-communities (Clans)", desc: "Groups of 15-30 with internal sociocratic governance. High autonomy." },
      { role: "Cross-cutting Circles", desc: "Land, infrastructure, economy, culture, conflict resolution — staffed from across clans." },
      { role: "Ombudsperson", desc: "Independent role for addressing grievances, power imbalances, and protecting minority voices." },
      { role: "Membership Council", desc: "Manages tribe-level admission, with each clan having veto for their own membership." },
    ],
    decisionMaking: "Consent within circles and clans. Council uses consent for tribe-wide policy. Constitutional decisions require 80% tribe-wide approval at general assembly.",
    conflictProtocol: [
      "1:1 conversation within 48 hours",
      "Clan-level mediation",
      "Cross-clan mediation via conflict resolution circle",
      "Ombudsperson investigation for systemic issues",
      "Tribal Council hearing as last resort",
      "Structured exit process with dignity provisions",
    ],
    rituals: ["Clan weekly circles", "Monthly tribal market/gathering", "Quarterly tribal assembly", "Bi-annual 'renewal of covenant'", "Annual celebration & visioning"],
    risks: ["Political factionalism", "Council power entrenchment", "Loss of intimacy", "Us-vs-them between clans", "Bureaucratic overhead"],
  },
};

// ─── STYLES ─────────────────────────────────────────────────────────────────

const font = `@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,700;1,9..144,400&family=DM+Sans:wght@300;400;500;600&display=swap');`;

// ─── COMPONENTS ─────────────────────────────────────────────────────────────

function ScoreRing({ score, size = 56, label, color }: { score: any; size?: number; label?: any; color?: any }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - score / 100);
  const c = color || (score >= 75 ? "#4d7c0f" : score >= 50 ? "#b45309" : "#dc2626");
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e7e5e4" strokeWidth="4" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={c} strokeWidth="4"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s ease" }} />
        <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
          style={{ transform: "rotate(90deg)", transformOrigin: "center", fontSize: size * 0.28, fontWeight: 600, fill: "#1c1917", fontFamily: "DM Sans" }}>
          {score}
        </text>
      </svg>
      {label && <span style={{ fontSize: 11, color: "#78716c", fontFamily: "DM Sans", fontWeight: 500 }}>{label}</span>}
    </div>
  );
}

function Pill({ children, active, onClick, color }: { children: any; active: any; onClick: any; color?: any }) {
  return (
    <button onClick={onClick} style={{
      padding: "6px 14px", borderRadius: 99, border: active ? "2px solid " + (color || "#b45309") : "1.5px solid #d6d3d1",
      background: active ? (color || "#b45309") + "15" : "white", color: active ? (color || "#92400e") : "#57534e",
      fontFamily: "DM Sans", fontSize: 13, fontWeight: active ? 600 : 400, cursor: "pointer", transition: "all 0.2s",
      whiteSpace: "nowrap"
    }}>
      {children}
    </button>
  );
}

function SliderQ({ question, value, onChange }) {
  const labels = ["Strongly\nDisagree", "Disagree", "Slightly\nDisagree", "Neutral", "Slightly\nAgree", "Agree", "Strongly\nAgree"];
  return (
    <div style={{ marginBottom: 28 }}>
      <p style={{ fontFamily: "DM Sans", fontSize: 15, color: "#1c1917", marginBottom: 12, lineHeight: 1.5 }}>{question}</p>
      <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
        {[1, 2, 3, 4, 5, 6, 7].map((n) => (
          <button key={n} onClick={() => onChange(n)} style={{
            width: 40, height: 40, borderRadius: 8,
            border: value === n ? "2px solid #b45309" : "1.5px solid #d6d3d1",
            background: value === n ? "#b45309" : "white",
            color: value === n ? "white" : "#78716c",
            fontFamily: "DM Sans", fontSize: 14, fontWeight: 600, cursor: "pointer",
            transition: "all 0.15s",
          }}>
            {n}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
        <span style={{ fontSize: 10, color: "#a8a29e", fontFamily: "DM Sans" }}>Disagree</span>
        <span style={{ fontSize: 10, color: "#a8a29e", fontFamily: "DM Sans" }}>Agree</span>
      </div>
    </div>
  );
}

// ─── PAGE: WELCOME ──────────────────────────────────────────────────────────

function WelcomePage({ onStart }) {
  return (
    <div style={{ textAlign: "center", maxWidth: 600, margin: "0 auto", padding: "40px 20px" }}>
      <div style={{ marginBottom: 20 }}>
        <svg width="80" height="80" viewBox="0 0 80 80">
          {/* Three interlocking circles — Borromean/triune: self, other, emergent we */}
          <circle cx="40" cy="28" r="18" fill="none" stroke="#1c1917" strokeWidth="2" opacity="0.7"/>
          <circle cx="28" cy="48" r="18" fill="none" stroke="#b45309" strokeWidth="2" opacity="0.7"/>
          <circle cx="52" cy="48" r="18" fill="none" stroke="#78716c" strokeWidth="2" opacity="0.7"/>
          {/* The center — where all three meet: the emergent third */}
          <circle cx="40" cy="41" r="5" fill="#b45309" opacity="0.15"/>
          {/* Three dots — one in each circle's unique space */}
          <circle cx="40" cy="22" r="2.5" fill="#1c1917"/>
          <circle cx="25" cy="52" r="2.5" fill="#b45309"/>
          <circle cx="55" cy="52" r="2.5" fill="#78716c"/>
        </svg>
      </div>
      <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 46, fontWeight: 700, color: "#1c1917", marginBottom: 0, lineHeight: 1.1, letterSpacing: "-1px" }}>
        tr<span style={{ color: "#b45309" }}>iii</span>be
        <span style={{ fontSize: 22, fontWeight: 300, color: "#a8a29e", verticalAlign: "super" }}>.me</span>
      </h1>
      <p style={{ fontFamily: "Fraunces, serif", fontSize: 16, color: "#78716c", fontStyle: "italic", marginBottom: 6, fontWeight: 300, marginTop: 10 }}>
        <span style={{ color: "#1c1917", fontWeight: 500 }}>i</span> · <span style={{ color: "#b45309", fontWeight: 500 }}>i</span> · <span style={{ color: "#78716c", fontWeight: 500 }}>i</span>
      </p>
      <p style={{ fontFamily: "DM Sans", fontSize: 13, color: "#a8a29e", marginBottom: 32, fontWeight: 400, lineHeight: 1.6 }}>
        self · other · the <em>we</em> that emerges between
      </p>
      <div style={{ background: "#fefce8", borderRadius: 16, padding: 28, marginBottom: 32, textAlign: "left", border: "1px solid #fde68a" }}>
        <p style={{ fontFamily: "DM Sans", fontSize: 14, color: "#44403c", lineHeight: 1.8, marginBottom: 16 }}>
          ~90% of intentional communities fail — mostly from <strong>poor matching</strong> and <strong>governance breakdown</strong>. triiibe.me addresses both:
        </p>
        <div style={{ display: "grid", gap: 12 }}>
          {[
            ["●", "#1c1917", "Know Yourself", "Big Five personality, core values, lifestyle & skills — the sovereign I, clarified."],
            ["●", "#b45309", "Find Your Others", "Compatibility matching across all four dimensions. The I that sees you."],
            ["●", "#78716c", "Grow the We", "Size-adaptive governance scaled to Dunbar's layers — the emergent third."],
          ].map(([dot, color, title, desc]) => (
            <div key={title} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <span style={{ fontSize: 18, flexShrink: 0, marginTop: 2, color }}>{dot}</span>
              <div>
                <strong style={{ fontFamily: "DM Sans", fontSize: 13, color: "#1c1917" }}>{title}</strong>
                <p style={{ fontFamily: "DM Sans", fontSize: 12.5, color: "#78716c", margin: "2px 0 0", lineHeight: 1.5 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <button onClick={onStart} style={{
        fontFamily: "DM Sans", fontSize: 16, fontWeight: 600, padding: "14px 40px",
        background: "#b45309", color: "white", border: "none", borderRadius: 12,
        cursor: "pointer", transition: "all 0.2s", boxShadow: "0 4px 14px #b4530940",
      }}>
        Begin →
      </button>
      <p style={{ fontFamily: "DM Sans", fontSize: 12, color: "#a8a29e", marginTop: 16 }}>
        3 minutes · your data stays with you
      </p>
    </div>
  );
}

// ─── PAGE: PROFILE WIZARD ───────────────────────────────────────────────────

function ProfileWizard({ onComplete, existingProfile }) {
  const [step, setStep] = useState(0);
  const [fullName, setFullName] = useState(existingProfile?.fullName || "");
  const [tribalName, setTribalName] = useState(existingProfile?.tribalName || "");
  const [bio, setBio] = useState(existingProfile?.bio || "");
  const [answers, setAnswers] = useState(existingProfile?._answers || Array(10).fill(0));
  const [values, setValues] = useState(existingProfile?.values || []);
  const [lifestyle, setLifestyle] = useState(existingProfile?.lifestyle || {
    sleep: "", noise: "", diet: "", cleanliness: 3, social: "", privacy: 3, meals: true, pets: true,
  });
  const [skills, setSkills] = useState(existingProfile?.skills || []);
  const [communitySize, setCommunitySize] = useState(existingProfile?.communitySize || "clan");
  const [locations, setLocations] = useState(existingProfile?.locations || [{ label: "", type: "open" }]);
  const [relocation, setRelocation] = useState(existingProfile?.relocation || "open");
  const [connectionPref, setConnectionPref] = useState(existingProfile?.connectionPref || "gradual");

  const steps = ["Basics", "Location", "Personality", "Values", "Lifestyle", "Skills"];

  const computePersonality = () => {
    const traits = {};
    ["openness", "conscientiousness", "extraversion", "agreeableness", "neuroticism"].forEach((trait) => {
      const qs = BIG_FIVE_QUESTIONS.map((q, i) => ({ ...q, idx: i })).filter((q) => q.trait === trait);
      let sum = 0;
      qs.forEach((q) => {
        const raw = answers[q.idx] || 4;
        sum += q.dir === 1 ? raw : 8 - raw;
      });
      traits[trait] = Math.round(sum / qs.length);
    });
    return traits;
  };

  const handleFinish = () => {
    const personality = computePersonality();
    // Display name: use tribal name if provided, otherwise first name
    const displayName = tribalName.trim() || fullName.trim().split(/\s+/)[0];
    onComplete({
      id: existingProfile?.id || "user-" + Date.now(),
      fullName, tribalName, name: displayName, bio, avatar: "👤",
      personality, values, lifestyle, skills, communitySize,
      locations: locations.filter((l) => l.label.trim()),
      relocation,
      connectionPref,
      _answers: answers,
    });
  };

  const canProceed = () => {
    if (step === 0) return fullName.trim().length > 0;
    if (step === 1) return locations.some((l) => l.label.trim().length > 0);
    if (step === 2) return answers.every((a) => a > 0);
    if (step === 3) return values.length >= 3;
    if (step === 4) return lifestyle.sleep && lifestyle.noise && lifestyle.diet && lifestyle.social;
    if (step === 5) return skills.length >= 2;
    return true;
  };

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "24px 20px" }}>
      {/* Progress */}
      <div style={{ display: "flex", gap: 4, marginBottom: 32 }}>
        {steps.map((s, i) => (
          <div key={s} style={{ flex: 1, textAlign: "center" }}>
            <div style={{
              height: 4, borderRadius: 4, marginBottom: 6,
              background: i <= step ? "#b45309" : "#e7e5e4",
              transition: "background 0.3s",
            }} />
            <span style={{ fontFamily: "DM Sans", fontSize: 11, color: i <= step ? "#b45309" : "#a8a29e", fontWeight: i === step ? 600 : 400 }}>{s}</span>
          </div>
        ))}
      </div>

      {/* Step 0: Basics */}
      {step === 0 && (
        <div>
          <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: "#1c1917", marginBottom: 8 }}>Who are you?</h2>
          <p style={{ fontFamily: "DM Sans", fontSize: 14, color: "#78716c", marginBottom: 24 }}>The foundation of finding your tribe starts here.</p>
          <label style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 500, color: "#44403c", display: "block", marginBottom: 6 }}>Your name</label>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="First and last name"
            style={{ width: "100%", padding: "12px 16px", borderRadius: 10, border: "1.5px solid #d6d3d1", fontFamily: "DM Sans", fontSize: 15, outline: "none", boxSizing: "border-box", marginBottom: 20 }} />
          <label style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 500, color: "#44403c", display: "block", marginBottom: 6 }}>
            Tribal name <span style={{ fontWeight: 400, color: "#a8a29e" }}>(optional — a chosen name others see instead of your real name)</span>
          </label>
          <input value={tribalName} onChange={(e) => setTribalName(e.target.value)} placeholder="e.g. SunWalker, River, Phoenix"
            style={{ width: "100%", padding: "12px 16px", borderRadius: 10, border: "1.5px solid #d6d3d1", fontFamily: "DM Sans", fontSize: 15, outline: "none", boxSizing: "border-box", marginBottom: 4 }} />
          <p style={{ fontFamily: "DM Sans", fontSize: 11, color: "#a8a29e", marginBottom: 20, marginTop: 0 }}>
            {tribalName.trim()
              ? `Others will see you as "${tribalName.trim()}"`
              : `If left blank, others will see your first name "${fullName.trim().split(/\s+/)[0] || "..."}"`}
          </p>
          <label style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 500, color: "#44403c", display: "block", marginBottom: 6 }}>Short bio <span style={{ fontWeight: 400, color: "#a8a29e" }}>(optional)</span></label>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="What draws you to community living?"
            style={{ width: "100%", padding: "12px 16px", borderRadius: 10, border: "1.5px solid #d6d3d1", fontFamily: "DM Sans", fontSize: 14, outline: "none", minHeight: 80, resize: "vertical", boxSizing: "border-box", marginBottom: 20 }} />
          <label style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 500, color: "#44403c", display: "block", marginBottom: 10 }}>Ideal community scale</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[["band", "Band (5-15)", "Intimate family"], ["clan", "Clan (15-50)", "Village core"], ["tribe", "Tribe (50-150)", "Full village"]].map(([k, label, sub]) => (
              <button key={k} onClick={() => setCommunitySize(k)} style={{
                flex: "1 1 120px", padding: "14px 12px", borderRadius: 12, border: communitySize === k ? "2px solid #b45309" : "1.5px solid #d6d3d1",
                background: communitySize === k ? "#fef3c7" : "white", cursor: "pointer", textAlign: "center",
              }}>
                <div style={{ fontFamily: "DM Sans", fontSize: 14, fontWeight: 600, color: "#1c1917" }}>{label}</div>
                <div style={{ fontFamily: "DM Sans", fontSize: 11, color: "#78716c", marginTop: 2 }}>{sub}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 1: Location */}
      {step === 1 && (
        <div>
          <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: "#1c1917", marginBottom: 4 }}>Where?</h2>
          <p style={{ fontFamily: "DM Sans", fontSize: 14, color: "#78716c", marginBottom: 24 }}>
            Community lives somewhere. Where are you looking to root — or willing to go?
          </p>

          {locations.map((loc, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <input value={loc.label} placeholder={i === 0 ? "City, region, or 'anywhere'" : "Another option..."}
                  onChange={(e) => {
                    const next = [...locations];
                    next[i] = { ...next[i], label: e.target.value };
                    setLocations(next);
                  }}
                  style={{ width: "100%", padding: "12px 16px", borderRadius: 10, border: "1.5px solid #d6d3d1", fontFamily: "DM Sans", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ display: "flex", gap: 4, flexShrink: 0, marginTop: 4 }}>
                {[["rooted", "📍", "I'm here"], ["open", "🌍", "Open to it"], ["dream", "✦", "Dream spot"]].map(([t, icon, tip]) => (
                  <button key={t} title={tip} onClick={() => {
                    const next = [...locations];
                    next[i] = { ...next[i], type: t };
                    setLocations(next);
                  }} style={{
                    width: 36, height: 36, borderRadius: 8, border: loc.type === t ? "2px solid #b45309" : "1.5px solid #d6d3d1",
                    background: loc.type === t ? "#fef3c7" : "white", cursor: "pointer", fontSize: 16,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>{icon}</button>
                ))}
              </div>
              {i > 0 && (
                <button onClick={() => setLocations(locations.filter((_, j) => j !== i))}
                  style={{ width: 36, height: 36, borderRadius: 8, border: "1.5px solid #d6d3d1", background: "white", cursor: "pointer", fontSize: 14, color: "#a8a29e", flexShrink: 0 }}>✕</button>
              )}
            </div>
          ))}

          {locations.length < 4 && (
            <button onClick={() => setLocations([...locations, { label: "", type: "open" }])}
              style={{
                padding: "8px 16px", borderRadius: 8, border: "1.5px dashed #d6d3d1", background: "white",
                fontFamily: "DM Sans", fontSize: 13, color: "#78716c", cursor: "pointer", marginBottom: 20,
              }}>+ Add another location</button>
          )}

          <div style={{ background: "#fefce8", borderRadius: 10, padding: 12, marginBottom: 20 }}>
            <div style={{ fontFamily: "DM Sans", fontSize: 11, color: "#92400e", lineHeight: 1.6 }}>
              📍 <strong>Rooted</strong> = I currently live here&nbsp;&nbsp;
              🌍 <strong>Open</strong> = I'd relocate here&nbsp;&nbsp;
              ✦ <strong>Dream</strong> = My ideal, if a tribe forms there
            </div>
          </div>

          <label style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 500, color: "#44403c", display: "block", marginBottom: 10 }}>Relocation willingness</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
            {[
              ["rooted", "Rooted — not moving", "Seeking community where I am"],
              ["open", "Open — would relocate", "For the right tribe, I'd move"],
              ["nomad", "Nomadic / digital", "Location-flexible, can join anywhere"],
            ].map(([k, label, sub]) => (
              <button key={k} onClick={() => setRelocation(k)} style={{
                flex: "1 1 160px", padding: "12px", borderRadius: 10, textAlign: "left",
                border: relocation === k ? "2px solid #b45309" : "1.5px solid #d6d3d1",
                background: relocation === k ? "#fef3c7" : "white", cursor: "pointer",
              }}>
                <div style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 600, color: "#1c1917" }}>{label}</div>
                <div style={{ fontFamily: "DM Sans", fontSize: 11, color: "#78716c", marginTop: 2 }}>{sub}</div>
              </button>
            ))}
          </div>

          <label style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 500, color: "#44403c", display: "block", marginBottom: 10 }}>How do you want to connect with matches?</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              ["gradual", "Gradual trust", "Circle intro → group chat → 1:1 after mutual interest"],
              ["open_dm", "Open to DMs", "Anyone with 60%+ match can message me directly"],
              ["tribe_first", "Tribe first", "I want to join a forming tribe, not individual outreach"],
            ].map(([k, label, sub]) => (
              <button key={k} onClick={() => setConnectionPref(k)} style={{
                flex: "1 1 160px", padding: "12px", borderRadius: 10, textAlign: "left",
                border: connectionPref === k ? "2px solid #4d7c0f" : "1.5px solid #d6d3d1",
                background: connectionPref === k ? "#f0fdf4" : "white", cursor: "pointer",
              }}>
                <div style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 600, color: "#1c1917" }}>{label}</div>
                <div style={{ fontFamily: "DM Sans", fontSize: 11, color: "#78716c", marginTop: 2 }}>{sub}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Personality */}
      {step === 2 && (
        <div>
          <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: "#1c1917", marginBottom: 4 }}>Your Personality</h2>
          <p style={{ fontFamily: "DM Sans", fontSize: 14, color: "#78716c", marginBottom: 24 }}>
            Based on the Big Five model — the most scientifically validated personality framework. Rate how much you agree with each statement.
          </p>
          {BIG_FIVE_QUESTIONS.map((q, i) => (
            <SliderQ key={i} question={q.text} value={answers[i]}
              onChange={(v) => { const a = [...answers]; a[i] = v; setAnswers(a); }} />
          ))}
        </div>
      )}

      {/* Step 3: Values */}
      {step === 3 && (
        <div>
          <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: "#1c1917", marginBottom: 4 }}>Core Values</h2>
          <p style={{ fontFamily: "DM Sans", fontSize: 14, color: "#78716c", marginBottom: 8 }}>
            Shared values are the #1 predictor of community longevity. Choose your top 5 (minimum 3).
          </p>
          <p style={{ fontFamily: "DM Sans", fontSize: 12, color: values.length >= 3 ? "#4d7c0f" : "#b45309", marginBottom: 16, fontWeight: 500 }}>
            {values.length}/5 selected {values.length < 3 && "• need at least 3"}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {VALUES_LIST.map((v) => {
              const active = values.includes(v.id);
              return (
                <button key={v.id} onClick={() => {
                  if (active) setValues(values.filter((x) => x !== v.id));
                  else if (values.length < 5) setValues([...values, v.id]);
                }} style={{
                  padding: "8px 14px", borderRadius: 10, cursor: values.length >= 5 && !active ? "not-allowed" : "pointer",
                  border: active ? "2px solid #4d7c0f" : "1.5px solid #d6d3d1",
                  background: active ? "#4d7c0f12" : "white",
                  opacity: values.length >= 5 && !active ? 0.4 : 1,
                  fontFamily: "DM Sans", fontSize: 13, color: active ? "#365314" : "#57534e",
                  fontWeight: active ? 600 : 400, transition: "all 0.15s",
                }}>
                  {v.icon} {v.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 4: Lifestyle */}
      {step === 4 && (
        <div>
          <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: "#1c1917", marginBottom: 4 }}>Lifestyle Preferences</h2>
          <p style={{ fontFamily: "DM Sans", fontSize: 14, color: "#78716c", marginBottom: 24 }}>The practical stuff that makes or breaks daily harmony.</p>

          {[
            ["sleep", "Sleep Schedule", [["early", "🌅 Early Bird"], ["owl", "🌙 Night Owl"], ["flexible", "🔄 Flexible"]]],
            ["noise", "Noise Preference", [["quiet", "🤫 Quiet"], ["moderate", "🔉 Moderate"], ["lively", "🎶 Lively"]]],
            ["diet", "Dietary Preference", [["vegan", "🌱 Vegan"], ["vegetarian", "🥚 Vegetarian"], ["flexible", "🍽 Flexitarian"], ["omnivore", "🥩 Omnivore"]]],
            ["social", "Social Frequency", [["daily", "Every day"], ["few", "Few times/week"], ["weekly", "Weekly"], ["minimal", "Minimal"]]],
          ].map(([key, label, opts]: any) => (
            <div key={key} style={{ marginBottom: 20 }}>
              <label style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 500, color: "#44403c", display: "block", marginBottom: 8 }}>{label}</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {(opts as any[]).map(([val, lbl]: any) => (
                  <Pill key={val} active={lifestyle[key] === val} onClick={() => setLifestyle({ ...lifestyle, [key]: val })}>{lbl}</Pill>
                ))}
              </div>
            </div>
          ))}

          {[
            ["cleanliness", "Cleanliness Standard", "Relaxed", "Spotless"],
            ["privacy", "Privacy Need", "Open door", "High privacy"],
          ].map(([key, label, lo, hi]) => (
            <div key={key} style={{ marginBottom: 20 }}>
              <label style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 500, color: "#44403c", display: "block", marginBottom: 8 }}>
                {label}: <strong>{lifestyle[key]}/5</strong>
              </label>
              <input type="range" min={1} max={5} value={lifestyle[key]} onChange={(e) => setLifestyle({ ...lifestyle, [key]: +e.target.value })}
                style={{ width: "100%", accentColor: "#b45309" }} />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, color: "#a8a29e", fontFamily: "DM Sans" }}>{lo}</span>
                <span style={{ fontSize: 11, color: "#a8a29e", fontFamily: "DM Sans" }}>{hi}</span>
              </div>
            </div>
          ))}

          <div style={{ display: "flex", gap: 20, marginTop: 8 }}>
            {[["meals", "Shared meals?"], ["pets", "Pet-friendly?"]].map(([key, label]) => (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "DM Sans", fontSize: 13, color: "#44403c", cursor: "pointer" }}>
                <input type="checkbox" checked={lifestyle[key]} onChange={(e) => setLifestyle({ ...lifestyle, [key]: e.target.checked })}
                  style={{ width: 18, height: 18, accentColor: "#b45309" }} />
                {label}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Step 5: Skills */}
      {step === 5 && (
        <div>
          <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: "#1c1917", marginBottom: 4 }}>Skills & Contributions</h2>
          <p style={{ fontFamily: "DM Sans", fontSize: 14, color: "#78716c", marginBottom: 8 }}>
            Resilient communities need diverse skills. What can you bring? (Pick 2-6)
          </p>
          <p style={{ fontFamily: "DM Sans", fontSize: 12, color: skills.length >= 2 ? "#4d7c0f" : "#b45309", marginBottom: 16, fontWeight: 500 }}>
            {skills.length}/6 selected {skills.length < 2 && "• need at least 2"}
          </p>
          {["land", "domestic", "care", "modern", "creative", "governance"].map((cat) => (
            <div key={cat} style={{ marginBottom: 14 }}>
              <span style={{ fontFamily: "DM Sans", fontSize: 11, fontWeight: 600, color: "#a8a29e", textTransform: "uppercase", letterSpacing: 1 }}>
                {cat === "land" ? "Land & Infrastructure" : cat === "domestic" ? "Home & Nourishment" : cat === "care" ? "Care & Wellbeing" : cat === "modern" ? "Modern Skills" : cat === "creative" ? "Creative" : "Governance"}
              </span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {SKILLS_LIST.filter((s) => s.cat === cat).map((s) => {
                  const active = skills.includes(s.id);
                  return (
                    <Pill key={s.id} active={active} color="#4d7c0f" onClick={() => {
                      if (active) setSkills(skills.filter((x) => x !== s.id));
                      else if (skills.length < 6) setSkills([...skills, s.id]);
                    }}>{s.label}</Pill>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Navigation */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 32, gap: 12 }}>
        <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
          style={{
            padding: "12px 24px", borderRadius: 10, border: "1.5px solid #d6d3d1", background: "white",
            fontFamily: "DM Sans", fontSize: 14, fontWeight: 500, cursor: step === 0 ? "not-allowed" : "pointer",
            color: step === 0 ? "#d6d3d1" : "#57534e",
          }}>← Back</button>
        {step < 5 ? (
          <button onClick={() => setStep(step + 1)} disabled={!canProceed()}
            style={{
              padding: "12px 32px", borderRadius: 10, border: "none",
              background: canProceed() ? "#b45309" : "#d6d3d1", color: "white",
              fontFamily: "DM Sans", fontSize: 14, fontWeight: 600, cursor: canProceed() ? "pointer" : "not-allowed",
              boxShadow: canProceed() ? "0 4px 14px #b4530940" : "none",
            }}>Next →</button>
        ) : (
          <button onClick={handleFinish} disabled={!canProceed()}
            style={{
              padding: "12px 32px", borderRadius: 10, border: "none",
              background: canProceed() ? "#4d7c0f" : "#d6d3d1", color: "white",
              fontFamily: "DM Sans", fontSize: 14, fontWeight: 600, cursor: canProceed() ? "pointer" : "not-allowed",
              boxShadow: canProceed() ? "0 4px 14px #4d7c0f40" : "none",
            }}>Find My Triiibe ⟡</button>
        )}
      </div>
    </div>
  );
}

// ─── CONNECTION TRUST LADDER PANEL ──────────────────────────────────────────

const TRUST_STAGES = [
  { key: "viewed", label: "Browse", icon: "◌", desc: "View profile & compatibility" },
  { key: "interested", label: "Signal", icon: "◐", desc: "Express interest with a wave" },
  { key: "mutual", label: "Mutual", icon: "◑", desc: "Both interested — connection begins" },
  { key: "intro_circle", label: "Circle", icon: "◍", desc: "Small group intro (3-6 people)" },
  { key: "direct", label: "Direct", icon: "●", desc: "1:1 messaging unlocked" },
  { key: "tribe_peer", label: "Tribe", icon: "⊛", desc: "Full tribe member access" },
];

function ConnectionPanel({ matchName, matchPref, myPref, compatScore }) {
  const [currentStage, setCurrentStage] = useState("viewed");
  const [waveText, setWaveText] = useState("");
  const [waveSent, setWaveSent] = useState(false);

  // Determine what happens after mutual based on prefs
  const resolvedPath = (myPref === "open_dm" && matchPref === "open_dm")
    ? "direct" // both open → skip to DM
    : (myPref === "tribe_first" || matchPref === "tribe_first")
      ? "tribe_first" // either wants tribe-first
      : "gradual"; // default → intro circle

  const stageIndex = TRUST_STAGES.findIndex((s) => s.key === currentStage);

  const handleWave = () => {
    setCurrentStage("interested");
    setWaveSent(true);
    // TODO: call expressInterest() API
  };

  const handleSimulateMutual = () => {
    if (resolvedPath === "direct") setCurrentStage("direct");
    else if (resolvedPath === "tribe_first") setCurrentStage("mutual");
    else setCurrentStage("intro_circle");
    // TODO: triggered automatically by DB trigger when both express interest
  };

  return (
    <div style={{
      marginTop: 16, background: "#fafaf9", borderRadius: 12, padding: 16,
      border: "1px solid #e7e5e4",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontFamily: "DM Sans", fontSize: 12, fontWeight: 600, color: "#44403c", textTransform: "uppercase", letterSpacing: 1 }}>
          Trust Ladder
        </span>
        <span style={{ fontFamily: "DM Sans", fontSize: 11, color: "#a8a29e" }}>
          {matchName}'s pref: {matchPref === "open_dm" ? "Open DMs" : matchPref === "tribe_first" ? "Tribe first" : "Gradual trust"}
        </span>
      </div>

      {/* Stage progress */}
      <div style={{ display: "flex", gap: 2, marginBottom: 16 }}>
        {TRUST_STAGES.map((s, i) => (
          <div key={s.key} style={{ flex: 1, textAlign: "center" }}>
            <div style={{
              height: 4, borderRadius: 2, marginBottom: 6,
              background: i <= stageIndex
                ? (i <= 1 ? "#b45309" : i <= 3 ? "#4d7c0f" : "#166534")
                : "#e7e5e4",
              transition: "background 0.4s",
            }} />
            <div style={{
              fontFamily: "DM Sans", fontSize: 15,
              color: i <= stageIndex ? "#1c1917" : "#d6d3d1",
              transition: "color 0.3s",
            }}>{s.icon}</div>
            <div style={{
              fontFamily: "DM Sans", fontSize: 9, marginTop: 2,
              color: i === stageIndex ? "#b45309" : i < stageIndex ? "#4d7c0f" : "#a8a29e",
              fontWeight: i === stageIndex ? 600 : 400,
            }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Current stage description */}
      <div style={{
        background: "white", borderRadius: 8, padding: 12, marginBottom: 12,
        border: "1px solid #e7e5e4",
      }}>
        <div style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 600, color: "#1c1917", marginBottom: 4 }}>
          {TRUST_STAGES[stageIndex]?.icon} {TRUST_STAGES[stageIndex]?.label} Stage
        </div>
        <div style={{ fontFamily: "DM Sans", fontSize: 12, color: "#57534e", lineHeight: 1.5 }}>
          {TRUST_STAGES[stageIndex]?.desc}
          {currentStage === "intro_circle" && (
            <span style={{ display: "block", marginTop: 4, color: "#78716c", fontStyle: "italic" }}>
              After 7 days in a shared intro circle, direct messaging unlocks.
            </span>
          )}
          {resolvedPath === "tribe_first" && currentStage === "mutual" && (
            <span style={{ display: "block", marginTop: 4, color: "#78716c", fontStyle: "italic" }}>
              {matchPref === "tribe_first" ? matchName : "You"} prefer{matchPref === "tribe_first" ? "s" : ""} to connect through a forming tribe rather than 1:1.
            </span>
          )}
        </div>
      </div>

      {/* Actions based on stage */}
      {currentStage === "viewed" && (
        <div>
          <div style={{ marginBottom: 10 }}>
            <input value={waveText} onChange={(e) => setWaveText(e.target.value.slice(0, 280))}
              placeholder={`Wave to ${matchName} (optional, 280 chars)`}
              style={{
                width: "100%", padding: "10px 14px", borderRadius: 8, border: "1.5px solid #d6d3d1",
                fontFamily: "DM Sans", fontSize: 13, outline: "none", boxSizing: "border-box",
              }} />
            <div style={{ fontFamily: "DM Sans", fontSize: 10, color: "#a8a29e", textAlign: "right", marginTop: 2 }}>
              {waveText.length}/280
            </div>
          </div>
          <button onClick={handleWave} style={{
            width: "100%", padding: "12px", borderRadius: 10, border: "none",
            background: "#b45309", color: "white", fontFamily: "DM Sans",
            fontSize: 14, fontWeight: 600, cursor: "pointer",
            boxShadow: "0 4px 14px #b4530940",
          }}>
            ◐ Signal Interest{compatScore >= 75 ? " — Strong Match!" : ""}
          </button>
        </div>
      )}

      {currentStage === "interested" && !waveSent && (
        <div style={{ fontFamily: "DM Sans", fontSize: 13, color: "#b45309", textAlign: "center", padding: 8 }}>
          Interest sent. Waiting for {matchName} to reciprocate...
        </div>
      )}

      {currentStage === "interested" && waveSent && (
        <div>
          <div style={{ fontFamily: "DM Sans", fontSize: 13, color: "#4d7c0f", textAlign: "center", padding: "8px 0", marginBottom: 8 }}>
            ✓ Wave sent to {matchName}
          </div>
          <button onClick={handleSimulateMutual} style={{
            width: "100%", padding: "10px", borderRadius: 8, border: "1.5px solid #d6d3d1",
            background: "white", fontFamily: "DM Sans", fontSize: 12, color: "#78716c",
            cursor: "pointer",
          }}>
            ↻ Simulate: {matchName} reciprocates
          </button>
        </div>
      )}

      {currentStage === "intro_circle" && (
        <div style={{
          background: "#f0fdf4", borderRadius: 8, padding: 12,
          border: "1px solid #bbf7d0", textAlign: "center",
        }}>
          <div style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 600, color: "#166534", marginBottom: 4 }}>
            ◍ Intro Circle Active
          </div>
          <div style={{ fontFamily: "DM Sans", fontSize: 12, color: "#4d7c0f" }}>
            You've been placed in a small group with {matchName} and 3 others who share your values. Group prompts start today.
          </div>
          <button onClick={() => setCurrentStage("direct")} style={{
            marginTop: 10, padding: "8px 20px", borderRadius: 8, border: "1.5px solid #bbf7d0",
            background: "white", fontFamily: "DM Sans", fontSize: 12, color: "#4d7c0f",
            cursor: "pointer",
          }}>
            ↻ Simulate: 7 days pass → unlock DM
          </button>
        </div>
      )}

      {currentStage === "direct" && (
        <div style={{
          background: "#f0fdf4", borderRadius: 8, padding: 12,
          border: "1px solid #bbf7d0", textAlign: "center",
        }}>
          <div style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 600, color: "#166534", marginBottom: 4 }}>
            ● Direct Message Unlocked
          </div>
          <div style={{ fontFamily: "DM Sans", fontSize: 12, color: "#4d7c0f", marginBottom: 8 }}>
            You and {matchName} can now message freely.
          </div>
          <button onClick={() => setCurrentStage("tribe_peer")} style={{
            padding: "8px 20px", borderRadius: 8, border: "none",
            background: "#4d7c0f", color: "white", fontFamily: "DM Sans", fontSize: 12,
            fontWeight: 600, cursor: "pointer",
          }}>
            Invite to Tribe
          </button>
        </div>
      )}

      {currentStage === "tribe_peer" && (
        <div style={{
          background: "linear-gradient(135deg, #fef3c7, #f0fdf4)", borderRadius: 8, padding: 12,
          border: "1px solid #bbf7d0", textAlign: "center",
        }}>
          <div style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 600, color: "#166534" }}>
            ⊛ Tribe Peers — full access
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PAGE: TRIBE FINDER ─────────────────────────────────────────────────────

function TribeFinder({ profile, allProfiles }) {
  const [selectedMatch, setSelectedMatch] = useState(null);

  const matches = useMemo(() => {
    return allProfiles
      .filter((p) => p.id !== profile.id)
      .map((p) => ({ profile: p, compat: computeCompatibility(profile, p) }))
      .sort((a, b) => b.compat.overall - a.compat.overall);
  }, [profile, allProfiles]);

  const topMatches = matches.slice(0, 8);

  // Suggest optimal tribe composition
  const suggestedTribe = useMemo(() => {
    const sizeMap = { band: 6, clan: 8, tribe: 8 };
    const target = sizeMap[profile.communitySize] || 6;
    // Greedy: pick top compatible, but also ensure skill diversity
    const picked = [];
    const skillsCovered = new Set(profile.skills);
    const sorted = [...matches];

    while (picked.length < target && sorted.length > 0) {
      // Score = compatibility + bonus for new skills
      let bestIdx = 0;
      let bestScore = -1;
      sorted.forEach((m, i) => {
        const newSkills = m.profile.skills.filter((s) => !skillsCovered.has(s)).length;
        const score = m.compat.overall + newSkills * 5;
        if (score > bestScore) { bestScore = score; bestIdx = i; }
      });
      const chosen = sorted.splice(bestIdx, 1)[0];
      picked.push(chosen);
      chosen.profile.skills.forEach((s) => skillsCovered.add(s));
    }
    return picked;
  }, [matches, profile]);

  const traitLabel = (trait, val) => {
    const labels = {
      openness: ["Conventional", "", "", "Balanced", "", "", "Exploratory"],
      conscientiousness: ["Spontaneous", "", "", "Balanced", "", "", "Structured"],
      extraversion: ["Introverted", "", "", "Ambivert", "", "", "Extraverted"],
      agreeableness: ["Challenger", "", "", "Balanced", "", "", "Harmonizer"],
      neuroticism: ["Unshakable", "", "", "Balanced", "", "", "Sensitive"],
    };
    return labels[trait]?.[val - 1] || "";
  };

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 20px" }}>
      <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: "#1c1917", marginBottom: 4 }}>
        Your Matches
      </h2>
      <p style={{ fontFamily: "DM Sans", fontSize: 14, color: "#78716c", marginBottom: 24 }}>
        Scored across personality, values, lifestyle & skills — the four threads of compatibility.
      </p>

      {/* Your personality snapshot */}
      <div style={{ background: "#fefce8", borderRadius: 14, padding: "16px 20px", marginBottom: 28, border: "1px solid #fde68a" }}>
        <span style={{ fontFamily: "DM Sans", fontSize: 12, fontWeight: 600, color: "#92400e", textTransform: "uppercase", letterSpacing: 1 }}>Your Big Five Profile</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 12 }}>
          {Object.entries(profile.personality).map(([trait, val]) => (
            <div key={trait} style={{ flex: "1 1 120px", minWidth: 100 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontFamily: "DM Sans", fontSize: 12, fontWeight: 500, color: "#44403c", textTransform: "capitalize" }}>{trait}</span>
                <span style={{ fontFamily: "DM Sans", fontSize: 11, color: "#78716c" }}>{traitLabel(trait, val)}</span>
              </div>
              <div style={{ height: 6, background: "#e7e5e4", borderRadius: 3 }}>
                <div style={{ height: 6, borderRadius: 3, background: "#b45309", width: `${(Number(val) / 7) * 100}%`, transition: "width 0.5s" }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Suggested Tribe */}
      {matches.length === 0 ? (
        <div style={{ background: "#fefce8", borderRadius: 14, padding: "32px 20px", marginBottom: 28, border: "1px solid #fde68a", textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>◌</div>
          <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 20, color: "#1c1917", margin: "0 0 8px" }}>Your profile is ready</h3>
          <p style={{ fontFamily: "DM Sans", fontSize: 14, color: "#78716c", lineHeight: 1.6, maxWidth: 400, margin: "0 auto 16px" }}>
            Once connected to Supabase, you'll see compatibility scores with every member of the community — ranked and grouped into your ideal tribe formation.
          </p>
          <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
            {["Personality 25%", "Values 35%", "Lifestyle 25%", "Skills 15%"].map((w) => (
              <span key={w} style={{
                padding: "4px 12px", borderRadius: 6, background: "white", border: "1px solid #fde68a",
                fontFamily: "DM Sans", fontSize: 12, color: "#92400e",
              }}>{w}</span>
            ))}
          </div>
        </div>
      ) : (
      <>
      <div style={{ background: "#f0fdf4", borderRadius: 14, padding: "16px 20px", marginBottom: 28, border: "1px solid #bbf7d0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontFamily: "DM Sans", fontSize: 12, fontWeight: 600, color: "#166534", textTransform: "uppercase", letterSpacing: 1 }}>
            ⟡ Suggested {GOVERNANCE_FRAMEWORKS[profile.communitySize]?.name || "Clan"} Formation
          </span>
          <span style={{ fontFamily: "DM Sans", fontSize: 11, color: "#4d7c0f" }}>
            Optimized for compatibility + skill diversity
          </span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <div style={{
            padding: "8px 14px", borderRadius: 10, background: "#b4530920", border: "1.5px solid #b45309",
            fontFamily: "DM Sans", fontSize: 13, fontWeight: 600, color: "#92400e"
          }}>
            {profile.avatar || "👤"} {profile.name} (you)
          </div>
          {suggestedTribe.map((m) => (
            <button key={m.profile.id} onClick={() => setSelectedMatch(m)}
              style={{
                padding: "8px 14px", borderRadius: 10, background: "white", border: "1.5px solid #bbf7d0",
                fontFamily: "DM Sans", fontSize: 13, color: "#1c1917", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6,
              }}>
              <span>{m.profile.avatar}</span>
              <span>{m.profile.name}</span>
              <span style={{ fontSize: 11, color: "#4d7c0f", fontWeight: 600 }}>{m.compat.overall}%</span>
            </button>
          ))}
        </div>
      </div>

      {/* Match Detail */}
      {selectedMatch && (
        <div style={{
          background: "white", borderRadius: 14, padding: 20, marginBottom: 24,
          border: "2px solid #b45309", boxShadow: "0 8px 24px #b4530915",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div>
              <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 22, color: "#1c1917", margin: 0 }}>
                {selectedMatch.profile.avatar} {selectedMatch.profile.name}
              </h3>
              <p style={{ fontFamily: "DM Sans", fontSize: 13, color: "#78716c", margin: "4px 0" }}>{selectedMatch.profile.bio}</p>
            </div>
            <button onClick={() => setSelectedMatch(null)} style={{
              background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#a8a29e", padding: 4,
            }}>✕</button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, justifyContent: "center", marginBottom: 16 }}>
            <ScoreRing score={selectedMatch.compat.overall} size={72} label="Overall" />
            <ScoreRing score={selectedMatch.compat.personality} size={56} label="Personality" />
            <ScoreRing score={selectedMatch.compat.values} size={56} label="Values" />
            <ScoreRing score={selectedMatch.compat.lifestyle} size={56} label="Lifestyle" />
            <ScoreRing score={selectedMatch.compat.skills} size={56} label="Skills" />
          </div>
          {selectedMatch.compat.sharedValues.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontFamily: "DM Sans", fontSize: 11, fontWeight: 600, color: "#4d7c0f" }}>SHARED VALUES: </span>
              {selectedMatch.compat.sharedValues.map((v) => {
                const val = VALUES_LIST.find((x) => x.id === v);
                return <span key={v} style={{ fontFamily: "DM Sans", fontSize: 12, color: "#365314" }}>{val?.icon} {val?.label}  </span>;
              })}
            </div>
          )}
          <div>
            <span style={{ fontFamily: "DM Sans", fontSize: 11, fontWeight: 600, color: "#78716c" }}>THEIR SKILLS: </span>
            {selectedMatch.profile.skills.map((s) => {
              const sk = SKILLS_LIST.find((x) => x.id === s);
              const isComplementary = !profile.skills.includes(s);
              return (
                <span key={s} style={{
                  fontFamily: "DM Sans", fontSize: 12,
                  color: isComplementary ? "#b45309" : "#57534e",
                  fontWeight: isComplementary ? 600 : 400,
                }}>
                  {sk?.label}{isComplementary ? " ★" : ""}{"  "}
                </span>
              );
            })}
            <p style={{ fontFamily: "DM Sans", fontSize: 11, color: "#a8a29e", marginTop: 4 }}>★ = complementary skill you don't have</p>
          </div>

          {/* Location overlap */}
          {selectedMatch.profile.locations && selectedMatch.profile.locations.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <span style={{ fontFamily: "DM Sans", fontSize: 11, fontWeight: 600, color: "#78716c" }}>LOCATIONS: </span>
              {selectedMatch.profile.locations.map((loc, i) => {
                const intentIcon = loc.type === "rooted" ? "📍" : loc.type === "dream" ? "✦" : "🌍";
                const isOverlap = profile.locations?.some(
                  (ml) => ml.label.trim().toLowerCase() === loc.label.trim().toLowerCase()
                );
                return (
                  <span key={i} style={{
                    fontFamily: "DM Sans", fontSize: 12,
                    color: isOverlap ? "#4d7c0f" : "#57534e",
                    fontWeight: isOverlap ? 600 : 400,
                  }}>
                    {intentIcon} {loc.label}{isOverlap ? " ◉" : ""}{"  "}
                  </span>
                );
              })}
              <p style={{ fontFamily: "DM Sans", fontSize: 11, color: "#a8a29e", marginTop: 2 }}>◉ = location you share</p>
            </div>
          )}

          {/* ── Connection Trust Ladder ── */}
          <ConnectionPanel
            matchName={selectedMatch.profile.name}
            matchPref={selectedMatch.profile.connectionPref}
            myPref={profile.connectionPref}
            compatScore={selectedMatch.compat.overall}
          />
        </div>
      )}

      {/* All Matches */}
      <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 20, color: "#1c1917", marginBottom: 12 }}>All Compatibility Scores</h3>
      <div style={{ display: "grid", gap: 8 }}>
        {topMatches.map((m) => (
          <button key={m.profile.id} onClick={() => setSelectedMatch(m)}
            style={{
              display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
              borderRadius: 12, border: selectedMatch?.profile.id === m.profile.id ? "2px solid #b45309" : "1.5px solid #e7e5e4",
              background: "white", cursor: "pointer", textAlign: "left", width: "100%",
            }}>
            <span style={{ fontSize: 28 }}>{m.profile.avatar}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "DM Sans", fontSize: 14, fontWeight: 600, color: "#1c1917" }}>
                {m.profile.name}
                {m.profile.locations && m.profile.locations.length > 0 && (
                  <span style={{ fontWeight: 400, fontSize: 11, color: "#a8a29e", marginLeft: 6 }}>
                    {m.profile.locations[0].type === "rooted" ? "📍" : "🌍"} {m.profile.locations[0].label}
                  </span>
                )}
              </div>
              <div style={{ fontFamily: "DM Sans", fontSize: 12, color: "#78716c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.profile.bio}</div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
              <ScoreRing score={m.compat.overall} size={44} />
            </div>
          </button>
        ))}
      </div>
      </>
      )}
    </div>
  );
}

// ─── PAGE: GOVERNANCE HUB ───────────────────────────────────────────────────

function GovernanceHub({ profile }) {
  const [activeSize, setActiveSize] = useState(profile?.communitySize || "clan");
  const fw = GOVERNANCE_FRAMEWORKS[activeSize];

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 20px" }}>
      <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: "#1c1917", marginBottom: 4 }}>Growing the We</h2>
      <p style={{ fontFamily: "DM Sans", fontSize: 14, color: "#78716c", marginBottom: 20 }}>
        Governance frameworks scaled to Dunbar's layers — because how you decide together <em>is</em> how you live together.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 28, flexWrap: "wrap" }}>
        {Object.entries(GOVERNANCE_FRAMEWORKS).map(([key, f]) => (
          <button key={key} onClick={() => setActiveSize(key)} style={{
            flex: "1 1 140px", padding: "12px 16px", borderRadius: 12, cursor: "pointer", textAlign: "center",
            border: activeSize === key ? "2px solid #b45309" : "1.5px solid #d6d3d1",
            background: activeSize === key ? "#fef3c7" : "white",
          }}>
            <div style={{ fontFamily: "DM Sans", fontSize: 15, fontWeight: 600, color: "#1c1917" }}>{f.name}</div>
            <div style={{ fontFamily: "DM Sans", fontSize: 12, color: "#78716c" }}>{f.size}</div>
          </button>
        ))}
      </div>

      {/* Model overview */}
      <div style={{ background: "#fefce8", borderRadius: 14, padding: 20, marginBottom: 24, border: "1px solid #fde68a" }}>
        <div style={{ fontFamily: "DM Sans", fontSize: 11, fontWeight: 600, color: "#92400e", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
          Recommended Model
        </div>
        <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 22, color: "#1c1917", margin: "0 0 4px" }}>{fw.model}</h3>
        <p style={{ fontFamily: "DM Sans", fontSize: 13, color: "#44403c", lineHeight: 1.6, margin: "0 0 8px" }}>{fw.description}</p>
        <p style={{ fontFamily: "DM Sans", fontSize: 12, color: "#78716c", fontStyle: "italic", margin: 0 }}>
          Dunbar layer: {fw.dunbar}
        </p>
      </div>

      {/* Structure */}
      <Section title="⚙️ Organizational Structure">
        {fw.structure.map((s, i) => (
          <div key={i} style={{ marginBottom: 12, paddingLeft: 16, borderLeft: "3px solid #b45309" }}>
            <div style={{ fontFamily: "DM Sans", fontSize: 14, fontWeight: 600, color: "#1c1917" }}>{s.role}</div>
            <div style={{ fontFamily: "DM Sans", fontSize: 13, color: "#57534e", lineHeight: 1.5 }}>{s.desc}</div>
          </div>
        ))}
      </Section>

      {/* Decision Making */}
      <Section title="🗳 Decision-Making Protocol">
        <p style={{ fontFamily: "DM Sans", fontSize: 13, color: "#44403c", lineHeight: 1.7 }}>{fw.decisionMaking}</p>
      </Section>

      {/* Conflict Resolution */}
      <Section title="🕊 Conflict Resolution Ladder">
        <div style={{ position: "relative", paddingLeft: 20 }}>
          {fw.conflictProtocol.map((step, i) => (
            <div key={i} style={{ display: "flex", gap: 12, marginBottom: 14, alignItems: "flex-start" }}>
              <div style={{
                width: 24, height: 24, borderRadius: 12, flexShrink: 0,
                background: i === 0 ? "#4d7c0f" : i < fw.conflictProtocol.length - 1 ? "#b45309" : "#dc2626",
                color: "white", fontFamily: "DM Sans", fontSize: 12, fontWeight: 600,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>{i + 1}</div>
              <span style={{ fontFamily: "DM Sans", fontSize: 13, color: "#44403c", lineHeight: 1.5 }}>{step}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Rituals */}
      <Section title="🔥 Community Rituals">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {fw.rituals.map((r, i) => (
            <span key={i} style={{
              padding: "6px 14px", borderRadius: 8, background: "#fef3c7", border: "1px solid #fde68a",
              fontFamily: "DM Sans", fontSize: 13, color: "#92400e",
            }}>{r}</span>
          ))}
        </div>
      </Section>

      {/* Risks */}
      <Section title="⚠️ Watch Out For">
        {fw.risks.map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "flex-start" }}>
            <span style={{ color: "#dc2626", fontSize: 12, marginTop: 2 }}>●</span>
            <span style={{ fontFamily: "DM Sans", fontSize: 13, color: "#57534e", lineHeight: 1.5 }}>{r}</span>
          </div>
        ))}
      </Section>

      {/* NVC callout */}
      <div style={{
        background: "linear-gradient(135deg, #fef3c7, #f0fdf4)", borderRadius: 14, padding: 20,
        marginTop: 24, border: "1px solid #bbf7d0",
      }}>
        <h4 style={{ fontFamily: "Fraunces, serif", fontSize: 18, color: "#1c1917", margin: "0 0 8px" }}>
          💬 Communication Foundation: NVC
        </h4>
        <p style={{ fontFamily: "DM Sans", fontSize: 13, color: "#44403c", lineHeight: 1.7, margin: 0 }}>
          Regardless of size, research shows <strong>Nonviolent Communication (NVC)</strong> is the strongest
          predictor of community longevity. NVC replaces judgment and demand with four steps:
          <strong> Observation → Feeling → Need → Request</strong>. Communities like Tui (NZ, 40+ years)
          require all members to be NVC-trained. Consider making it a membership prerequisite.
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, color: "#1c1917", marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid #e7e5e4" }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

// ─── RESULTS GATE ──────────────────────────────────────────────────────────
// After completing the profile wizard, users must verify identity with at
// least one contact method + pass an anti-bot challenge before receiving
// a link to their results.

function ResultsGate({ profile, onVerified }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [socials, setSocials] = useState([{ platform: "", handle: "" }]);
  const [botScore, setBotScore] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [challengeAnswer, setChallengeAnswer] = useState("");

  // Simple proof-of-humanity: random math challenge
  const [challengeA] = useState(() => Math.floor(Math.random() * 10) + 2);
  const [challengeB] = useState(() => Math.floor(Math.random() * 10) + 2);
  const correctAnswer = String(challengeA + challengeB);

  // Passive bot signals: track real interactions
  const [interactions, setInteractions] = useState(0);
  const [startTime] = useState(() => Date.now());
  const trackInteraction = () => setInteractions((n) => n + 1);

  const hasContact = email.trim().includes("@") || phone.trim().length >= 7 ||
    socials.some((s) => s.platform && s.handle.trim());

  const passesBot = () => {
    const timeSpent = (Date.now() - startTime) / 1000;
    // Must: answer challenge correctly, spend >3s, have >2 interactions
    return challengeAnswer === correctAnswer && timeSpent > 3 && interactions > 2;
  };

  const canSubmit = hasContact && challengeAnswer === correctAnswer;

  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!passesBot()) {
      alert("Something went wrong. Please try again.");
      return;
    }
    setSubmitting(true);
    setError("");

    const payload = {
      profileId: profile.id,
      tribalName: profile.name,
      fullName: profile.fullName,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      socials: socials.filter((s) => s.handle.trim()),
      profile,
      botScore: interactions,
    };

    try {
      const res = await fetch("/api/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Verification failed. Please try again.");
        setSubmitting(false);
        return;
      }

      // Mark as verified locally
      await storage.set("triiibe-contact", JSON.stringify({ verified: true, email: email.trim() }));
      setSubmitting(false);
      setSubmitted(true);
    } catch (e) {
      console.error("Verify error:", e);
      setError("Network error. Please check your connection and try again.");
      setSubmitting(false);
    }
  };

  if (submitted) {
    const destination = email.trim() ? email.trim() : phone.trim() ? "your phone" : "your preferred platform";
    return (
      <div style={{ maxWidth: 520, margin: "0 auto", padding: "60px 20px", textAlign: "center" }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>📬</div>
        <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: "#1c1917", marginBottom: 12 }}>
          Check your inbox
        </h2>
        <p style={{ fontFamily: "DM Sans", fontSize: 15, color: "#57534e", lineHeight: 1.7, marginBottom: 32 }}>
          We're preparing your tribe matches and will send a personal link to <strong>{destination}</strong>.
          This usually takes just a few minutes.
        </p>
        <div style={{
          background: "#fef3c7", borderRadius: 12, padding: 16, border: "1px solid #fde68a",
        }}>
          <p style={{ fontFamily: "DM Sans", fontSize: 13, color: "#92400e", margin: 0 }}>
            We verify every profile to keep triiibe a space for real people building real community.
            Your contact info is never shared with other members.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 520, margin: "0 auto", padding: "40px 20px" }}>
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔐</div>
        <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: "#1c1917", marginBottom: 8 }}>
          One last step, {profile.name}
        </h2>
        <p style={{ fontFamily: "DM Sans", fontSize: 14, color: "#78716c", lineHeight: 1.6 }}>
          To keep triiibe a trusted space, we verify every member before sharing match results.
          We'll send your personal results link to the contact you provide.
        </p>
      </div>

      <div style={{
        background: "white", borderRadius: 16, padding: 24, border: "1px solid #e7e5e4",
        marginBottom: 24,
      }}>
        <label style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 500, color: "#44403c", display: "block", marginBottom: 6 }}>
          Email <span style={{ fontWeight: 400, color: "#a8a29e" }}>(private)</span>
        </label>
        <input value={email} onChange={(e) => { setEmail(e.target.value); trackInteraction(); }}
          placeholder="you@example.com" type="email"
          style={{ width: "100%", padding: "12px 16px", borderRadius: 10, border: "1.5px solid #d6d3d1", fontFamily: "DM Sans", fontSize: 15, outline: "none", boxSizing: "border-box", marginBottom: 20 }} />

        <label style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 500, color: "#44403c", display: "block", marginBottom: 6 }}>
          Phone <span style={{ fontWeight: 400, color: "#a8a29e" }}>(optional, private)</span>
        </label>
        <input value={phone} onChange={(e) => { setPhone(e.target.value); trackInteraction(); }}
          placeholder="+1 (555) 123-4567" type="tel"
          style={{ width: "100%", padding: "12px 16px", borderRadius: 10, border: "1.5px solid #d6d3d1", fontFamily: "DM Sans", fontSize: 15, outline: "none", boxSizing: "border-box", marginBottom: 20 }} />

        <label style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 500, color: "#44403c", display: "block", marginBottom: 6 }}>
          Social handles <span style={{ fontWeight: 400, color: "#a8a29e" }}>(optional, private)</span>
        </label>
        {socials.map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <select value={s.platform} onChange={(e) => {
              const next = [...socials]; next[i] = { ...next[i], platform: e.target.value }; setSocials(next); trackInteraction();
            }} style={{ width: 140, padding: "12px 12px", borderRadius: 10, border: "1.5px solid #d6d3d1", fontFamily: "DM Sans", fontSize: 14, outline: "none", background: "white" }}>
              <option value="">Platform</option>
              <option value="instagram">Instagram</option>
              <option value="twitter">X / Twitter</option>
              <option value="tiktok">TikTok</option>
              <option value="facebook">Facebook</option>
              <option value="linkedin">LinkedIn</option>
              <option value="telegram">Telegram</option>
              <option value="signal">Signal</option>
              <option value="discord">Discord</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="other">Other</option>
            </select>
            <input value={s.handle} onChange={(e) => {
              const next = [...socials]; next[i] = { ...next[i], handle: e.target.value }; setSocials(next); trackInteraction();
            }} placeholder="@handle or link"
              style={{ flex: 1, padding: "12px 16px", borderRadius: 10, border: "1.5px solid #d6d3d1", fontFamily: "DM Sans", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
            {socials.length > 1 && (
              <button onClick={() => { setSocials(socials.filter((_, j) => j !== i)); trackInteraction(); }} style={{
                width: 36, height: 44, borderRadius: 10, border: "1.5px solid #d6d3d1", background: "white", cursor: "pointer", fontSize: 16, color: "#a8a29e",
              }}>×</button>
            )}
          </div>
        ))}
        <button onClick={() => { setSocials([...socials, { platform: "", handle: "" }]); trackInteraction(); }} style={{
          padding: "6px 14px", borderRadius: 8, border: "1.5px dashed #d6d3d1", background: "none",
          fontFamily: "DM Sans", fontSize: 12, color: "#78716c", cursor: "pointer", marginBottom: 8,
        }}>+ Add another</button>

        {!hasContact && (
          <p style={{ fontFamily: "DM Sans", fontSize: 12, color: "#b45309", marginTop: 12, marginBottom: 0 }}>
            Please provide at least one contact method so we can send your results.
          </p>
        )}
      </div>

      {/* Anti-bot: proof of humanity */}
      <div style={{
        background: "white", borderRadius: 16, padding: 24, border: "1px solid #e7e5e4",
        marginBottom: 24,
      }}>
        <label style={{ fontFamily: "DM Sans", fontSize: 13, fontWeight: 500, color: "#44403c", display: "block", marginBottom: 8 }}>
          Quick human check
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontFamily: "Fraunces, serif", fontSize: 22, color: "#1c1917" }}>
            {challengeA} + {challengeB} =
          </span>
          <input value={challengeAnswer}
            onChange={(e) => { setChallengeAnswer(e.target.value.replace(/[^0-9]/g, "")); trackInteraction(); }}
            placeholder="?"
            style={{ width: 64, padding: "10px 14px", borderRadius: 10, border: "1.5px solid #d6d3d1", fontFamily: "DM Sans", fontSize: 18, outline: "none", textAlign: "center" }}
            maxLength={3} />
          {challengeAnswer === correctAnswer && (
            <span style={{ color: "#4d7c0f", fontSize: 20 }}>✓</span>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          background: "#fef2f2", borderRadius: 10, padding: 12, marginBottom: 16,
          border: "1px solid #fecaca",
        }}>
          <p style={{ fontFamily: "DM Sans", fontSize: 13, color: "#dc2626", margin: 0 }}>{error}</p>
        </div>
      )}

      <button onClick={handleSubmit} disabled={!canSubmit || submitting}
        style={{
          width: "100%", padding: "16px 24px", borderRadius: 12, border: "none",
          background: canSubmit ? "#b45309" : "#d6d3d1",
          color: canSubmit ? "white" : "#a8a29e",
          fontFamily: "DM Sans", fontSize: 16, fontWeight: 600,
          cursor: canSubmit ? "pointer" : "not-allowed",
          transition: "all 0.2s",
        }}>
        {submitting ? "Verifying..." : "Send me my results"}
      </button>

      <p style={{ fontFamily: "DM Sans", fontSize: 11, color: "#a8a29e", textAlign: "center", marginTop: 12, lineHeight: 1.5 }}>
        Your contact info is encrypted and never shared with other members.
        We only use it to verify your identity and deliver your results.
      </p>
    </div>
  );
}

// ─── MAIN APP ───────────────────────────────────────────────────────────────

export default function App() {
  const [view, setView] = useState("welcome"); // welcome | wizard | verify | dashboard
  const [tab, setTab] = useState("tribe"); // tribe | governance
  const [profile, setProfile] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Load profile from storage
  useEffect(() => {
    (async () => {
      try {
        const result = await storage.get("triiibe-profile");
        if (result?.value) {
          const p = JSON.parse(result.value);
          setProfile(p);
          // Check if verified
          const contact = await storage.get("triiibe-contact");
          if (contact?.value) {
            setView("dashboard");
          } else {
            setView("verify");
          }
        }
      } catch (e) {
        // No saved profile
      }
      setLoaded(true);
    })();
  }, []);

  const saveProfile = async (p) => {
    setProfile(p);
    try {
      await storage.set("triiibe-profile", JSON.stringify(p));
    } catch (e) {
      console.error("Storage error:", e);
    }
  };

  const handleProfileComplete = async (p) => {
    await saveProfile(p);
    // Check if already verified
    try {
      const contact = await storage.get("triiibe-contact");
      if (contact?.value) {
        setView("dashboard");
        setTab("tribe");
        return;
      }
    } catch (e) { /* not verified yet */ }
    setView("verify");
  };

  const handleVerified = () => {
    setView("dashboard");
    setTab("tribe");
  };

  // TODO: Replace with Supabase query — fetch all profiles from `profiles` table
  const [communityProfiles, setCommunityProfiles] = useState([]);
  const allProfiles = useMemo(() => {
    if (!profile) return [];
    return [profile, ...communityProfiles];
  }, [profile, communityProfiles]);

  if (!loaded) return null;

  const navItems = [
    { id: "tribe", label: "Find", icon: "◌" },
    { id: "governance", label: "Govern", icon: "◍" },
  ];

  return (
    <div style={{ minHeight: "100vh", fontFamily: "DM Sans, sans-serif", background: "#fafaf9" }}>
      <style>{font}</style>

      {view === "dashboard" && (
        <nav style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 20px", borderBottom: "1px solid #e7e5e4", background: "white",
          position: "sticky", top: 0, zIndex: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="22" height="22" viewBox="0 0 80 80" style={{ flexShrink: 0 }}>
              <circle cx="40" cy="28" r="16" fill="none" stroke="#1c1917" strokeWidth="3"/>
              <circle cx="29" cy="48" r="16" fill="none" stroke="#b45309" strokeWidth="3"/>
              <circle cx="51" cy="48" r="16" fill="none" stroke="#78716c" strokeWidth="3"/>
            </svg>
            <span style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: "#1c1917" }}>tr<span style={{ color: "#b45309" }}>iii</span>be<span style={{ fontSize: 12, fontWeight: 300, color: "#a8a29e" }}>.me</span></span>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {navItems.map((n) => (
              <button key={n.id} onClick={() => setTab(n.id)} style={{
                padding: "6px 16px", borderRadius: 8, border: "none",
                background: tab === n.id ? "#b4530915" : "transparent",
                color: tab === n.id ? "#b45309" : "#78716c",
                fontFamily: "DM Sans", fontSize: 13, fontWeight: tab === n.id ? 600 : 400,
                cursor: "pointer",
              }}>
                {n.icon} {n.label}
              </button>
            ))}
          </div>
          <button onClick={() => { setView("wizard"); }} style={{
            padding: "6px 14px", borderRadius: 8, border: "1.5px solid #d6d3d1",
            background: "white", fontFamily: "DM Sans", fontSize: 12, color: "#57534e",
            cursor: "pointer",
          }}>
            ✏️ Edit Profile
          </button>
        </nav>
      )}

      {view === "welcome" && <WelcomePage onStart={() => setView("wizard")} />}
      {view === "wizard" && <ProfileWizard onComplete={handleProfileComplete} existingProfile={profile} />}
      {view === "verify" && profile && <ResultsGate profile={profile} onVerified={handleVerified} />}
      {view === "dashboard" && tab === "tribe" && profile && <TribeFinder profile={profile} allProfiles={allProfiles} />}
      {view === "dashboard" && tab === "governance" && <GovernanceHub profile={profile} />}
    </div>
  );
}
